import React from 'react';
import { Link } from 'react-router-dom';
import PrimaryButton from '../../Buttons/PrimaryButton/PrimaryButton';
import Carousel from '../../Carousel/BasicCardCarousel';
import Accordion from '../../Accordion/Accordion';
import PricingTable from '../../PricingTable/PricingTable';
import macBookPlayerImage from '../../../../assets/images/device_images/macbook_bijan.svg';
import macBookDraftTrackerImage from '../../../../assets/images/device_images/macbook-draft-tracker.svg';
import macBookRosterProfileImage from '../../../../assets/images/device_images/macbook-roster-profile.svg';
import groupIPhoneImage from '../../../../assets/images/device_images/iPhoneGroup.svg';
import iPhonePlayerProfileSummary from '../../../../assets/images/device_images/iphone-player-profile-summary.jpg';
import iPhonePlayerProfileFFS from '../../../../assets/images/device_images/iphone-player-profile-ffs.jpg';
import iPhoneRosterIndex from '../../../../assets/images/device_images/iphone-roster-index.svg';
import iPhoneRosterProfileImage from '../../../../assets/images/device_images/iphone-roster-profile.svg';
import FitnessHubImage from '../../../../assets/images/fitness_hub.jpg';
import NutritionLabImage from '../../../../assets/images/nutrition_lab.jpg';
import ClassroomImage from '../../../../assets/images/classroom.jpg';
import BestBallZoneImage from '../../../../assets/images/bestballzone.jpg';
import FrontOfficeImage from '../../../../assets/images/best_gm_office2.jpg';

const FANTASY_FEATURES = [
	{
		icon: '🧠',
		title: 'AI Insights',
		description:
			'Player, team, and roster-level recommendations powered by data—not gut feelings.'
	},
	{
		icon: '🔄',
		title: 'Roster Sync™',
		description:
			'Connect Yahoo, Sleeper, or FFPC rosters and get instant, actionable team intelligence.'
	},
	{
		icon: '📋',
		title: 'Draft Command Center',
		description:
			'Draft Tracker, Roster Builder™, exposure tracking, and tools built for serious drafters.'
	},
	{
		icon: '🎯',
		title: 'Best Ball Arsenal',
		description:
			'Projections, Grade My Draft, Draft Build Spectrum, and Spike Volatility Index in one zone.'
	},
	{
		icon: '🔬',
		title: 'Deep Player Intel',
		description:
			'Prospect Score™, side-by-side compares, playoff opponent stacks, and rich player profiles.'
	},
	{
		icon: '🏆',
		title: 'Front Office Mode',
		description:
			'Your command center—rosters, alerts, friends, badges, and trophies in an immersive GM experience.'
	}
];

const LIFESTYLE_HUBS = [
	{
		title: 'Front Office',
		icon: '💼',
		image: FrontOfficeImage,
		description:
			'Step into the GM suite. Manage your fantasy life with the same intensity you bring to the gym.'
	},
	{
		title: 'Best Ball Zone',
		icon: '🎯',
		image: BestBallZoneImage,
		description:
			'Best-ball-specific tools in a dedicated space—draft smarter, stack smarter, win more.'
	},
	{
		title: 'The Classroom',
		icon: '🧑‍🏫',
		image: ClassroomImage,
		description:
			'Film room breakdowns, strategy whiteboards, and courses that sharpen your football IQ.'
	},
	{
		title: 'Fitness Hub',
		icon: '🏋️',
		image: FitnessHubImage,
		description:
			'Build fitness profiles, track progress, and take on friends through the Challenge Wire.'
	},
	{
		title: 'Nutrition Lab',
		icon: '🥗',
		image: NutritionLabImage,
		description:
			'Snap your fridge. Get AI-powered meal ideas from what you actually have on hand.'
	}
];

const FANTASY_PILLAR_BULLETS = [
	'Player & team AI insights',
	'Draft Tracker & Roster Builder™',
	'Prospect profiles & compare tools',
	'Best Ball Zone analytics suite',
	'Classroom film room & whiteboards',
	'Front Office command center'
];

const HEALTH_PILLAR_BULLETS = [
	'Fitness profiles & Challenge Wire',
	'Nutrition Lab fridge-to-recipe AI',
	'Friends, badges & trophies',
	'Workout plans & exercise library',
	'Personalized fitness questionnaires',
	'Immersive Fitness Hub & Nutrition Lab'
];

function PillarCard({ icon, title, description, bullets, accentClass }) {
	return (
		<div
			className={`rounded-2xl bg-white p-6 md:p-8 shadow-md border border-gray-200 border-l-4 ${accentClass}`}
		>
			<div className="flex items-center gap-3 mb-4">
				<span
					className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gray-100 border border-gray-200 text-2xl"
					aria-hidden
				>
					{icon}
				</span>
				<h4 className="text-xl md:text-2xl font-bold text-gray-900">{title}</h4>
			</div>
			<p className="text-sm md:text-base text-gray-600 leading-relaxed">{description}</p>
			<ul className="mt-5 space-y-2.5">
				{bullets.map((item) => (
					<li
						key={item}
						className="flex items-start gap-2.5 text-sm md:text-base text-gray-800"
					>
						<span
							className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-[11px] font-bold leading-none text-white"
							aria-hidden
						>
							✓
						</span>
						{item}
					</li>
				))}
			</ul>
		</div>
	);
}

const VALUE_PILLARS = [
	{
		icon: 'fas fa-bolt',
		title: 'Built for competitors',
		description:
			'Whether you are chasing a league trophy or a new PR, Fantasy Fitness is designed for people who show up to win.'
	},
	{
		icon: 'fas fa-layer-group',
		title: 'One platform, two games',
		description:
			'Fantasy tools and lifestyle hubs live together—no more juggling five apps to stay sharp on and off the field.'
	},
	{
		icon: 'fas fa-mobile-alt',
		title: 'Mobile-first',
		description:
			'Research players on the couch, log a challenge from the gym, or scan your fridge before dinner—all from your phone.'
	},
	{
		icon: 'fas fa-chart-line',
		title: 'Data you can act on',
		description:
			'From AI roster insights to nutrition intelligence, every feature is built to drive your next move.'
	}
];

export default function MarketingPage({
	stripe_product_essential_id,
	stripe_product_advanced_id,
	stripe_product_ultimate_id
}) {
	return (
		<div className="bg-white text-gray-900">
			{/* Hero */}
			<section className="relative overflow-hidden bg-white pt-12 pb-12 lg:pt-20 lg:pb-16">
				<div className="pointer-events-none absolute -right-32 top-0 h-96 w-96 rounded-full bg-blue-100/60 blur-3xl" />
				<div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-gray-100 blur-3xl" />
				<div className="relative grid grid-cols-12 h-full">
					<div className="col-span-12 xl:col-span-5 px-5 sm:px-6 md:px-8 ml-5 sm:ml-10 lg:ml-20 xl:ml-52 mr-5 xl:mr-20">
						<p className="mb-4 inline-block rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm md:text-base leading-snug sm:leading-normal font-medium tracking-tight sm:tracking-wide text-blue-700">
							Fantasy football meets real-world fitness
						</p>
						<h1
							className="font-bold mb-4 text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl 2xl:text-7xl"
							style={{ lineHeight: '1.15' }}
						>
							Dominate your fantasy leagues,
							<span className="block text-blue-600">and your health.</span>
						</h1>
						<p className="text-lg sm:text-xl xl:text-lg mb-8 font-light text-gray-600 leading-relaxed max-w-xl">
							Unparalleled data-driven player insights and advanced draft tools—plus
							fitness profiles, nutrition intelligence, and friendly challenges.
							One lifestyle app for people who treat winning like a habit.
						</p>
						<div className="flex flex-wrap items-center gap-4">
							<PrimaryButton
								label="See how it works"
								url="#work-rise-dominate"
								onShowcase={true}
							/>
							<Link
								to="/signup"
								className="text-lg font-medium text-blue-600 hover:text-blue-700 hover:underline"
							>
								Start free →
							</Link>
						</div>
					</div>
					<div className="col-span-12 xl:col-span-7 flex justify-center items-center pt-6 xl:pt-0 xl:mr-10">
						<img
							src={macBookPlayerImage}
							alt="Fantasy Fitness player profile on MacBook"
							className="w-3/4 hidden md:block"
						/>
					</div>
				</div>
			</section>

			{/* Brand manifesto + two pillars */}
			<section id="work-rise-dominate" className="bg-ff-black text-white">
				<div className="px-6 py-14 border-b border-white/10">
					<div className="container mx-auto max-w-3xl text-center">
						<p className="text-xs font-semibold uppercase tracking-widest text-blue-400 mb-4">
							The Fantasy Fitness mindset
						</p>
						<h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-5">
							Work. Rise. Dominate.
						</h2>
						<p className="text-base md:text-lg text-gray-400 leading-relaxed">
							Great fantasy managers and great athletes share the same DNA—discipline,
							preparation, and the drive to outwork everyone else.
						</p>
						<p className="mt-4 text-base md:text-lg text-gray-300 leading-relaxed">
							Fantasy Fitness is built for both: elite football tools for your leagues,
							and lifestyle hubs for your training, nutrition, and daily life.
						</p>
					</div>
				</div>

				<div className="px-6 py-14 md:py-16 bg-gray-50" id="two-pillars">
					<div className="container mx-auto max-w-6xl">
						<div className="text-center mb-10 md:mb-12">
							<h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-3">
								Two worlds. One winning standard.
							</h3>
							<p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
								Stop choosing between being the smartest manager in your league and the
								healthiest version of yourself. Fantasy Fitness unites both under one roof.
							</p>
						</div>

						<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
							<PillarCard
								icon="🏈"
								title="Dominate your fantasy leagues"
								description="From AI-powered player insights to Roster Sync™ across Yahoo, Sleeper, and FFPC—every tool is engineered to give you an edge on draft day, waiver wire Wednesday, and championship weekend."
								bullets={FANTASY_PILLAR_BULLETS}
								accentClass="border-l-blue-600"
							/>

							<PillarCard
								icon="💪"
								title="Dominate your health"
								description="Immersive lifestyle hubs turn self-improvement into something you actually want to open every day—track workouts, fuel smarter, and challenge friends who share your mindset."
								bullets={HEALTH_PILLAR_BULLETS}
								accentClass="border-l-emerald-600"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* Fantasy tools */}
			<section className="py-20 px-6" id="fantasy-tools">
				<div className="container mx-auto max-w-6xl">
					<div className="mb-12">
						<p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-4">
							Fantasy command center
						</p>
						<h2 className="text-4xl md:text-5xl font-bold mb-4 md:whitespace-nowrap">
							Elite tools for serious managers
						</h2>
						<p className="text-lg text-gray-600 max-w-4xl">
							Everything you need to research, draft, manage, and win—backed by the
							deepest player data and AI recommendations in the game.
						</p>
					</div>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
						{FANTASY_FEATURES.map((feature) => (
							<div
								key={feature.title}
								className="rounded-xl border border-gray-200 bg-gray-50 p-6 hover:border-blue-200 hover:shadow-md transition-all"
							>
								<span className="text-3xl" aria-hidden>
									{feature.icon}
								</span>
								<h3 className="mt-3 text-xl font-semibold">{feature.title}</h3>
								<p className="mt-2 text-gray-600 text-sm leading-relaxed">
									{feature.description}
								</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Lifestyle hubs */}
			<section className="py-16 px-6 bg-gray-50" id="lifestyle-hubs">
				<div className="container mx-auto max-w-6xl">
					<div className="text-center mb-14">
						<p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-4">
							Lifestyle hubs
						</p>
						<h2 className="text-4xl md:text-5xl font-bold mb-4">
							Immersive spaces for life off the leaderboard
						</h2>
						<p className="text-lg text-gray-600 max-w-3xl mx-auto">
							Five beautifully designed hubs—each a destination, not a settings page.
							Explore them all when you join Fantasy Fitness.
						</p>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
						{LIFESTYLE_HUBS.map((hub) => (
							<div
								key={hub.title}
								className="group overflow-hidden rounded-2xl bg-white shadow-lg border border-gray-100"
							>
								<div className="relative h-48 overflow-hidden">
									<img
										src={hub.image}
										alt={hub.title}
										loading="lazy"
										decoding="async"
										className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
									/>
									<span className="absolute top-3 left-3 rounded-full bg-black/50 px-3 py-1 text-2xl backdrop-blur-sm">
										{hub.icon}
									</span>
								</div>
								<div className="p-5">
									<h3 className="text-xl font-bold">{hub.title}</h3>
									<p className="mt-2 text-sm text-gray-600 leading-relaxed">
										{hub.description}
									</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Product showcase */}
			<section
				id="introducing-fantasy-fitness"
				className="bg-white container text-center py-16 px-6"
			>
				<p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-4">
					See it in action
				</p>
				<h2 className="text-4xl md:text-5xl mb-4 font-bold">More Fantasy Fitness</h2>
				<p className="text-lg text-gray-600 max-w-2xl mx-auto mb-12">
					Powerful on desktop. Essential in your pocket. Manage rosters, scout players,
					and stay locked in—from the draft room to the weight room.
				</p>
				<Carousel
					slideSpeed={5000}
					images={[
						groupIPhoneImage,
						macBookDraftTrackerImage,
						macBookRosterProfileImage,
						groupIPhoneImage
					]}
					mobileImages={[
						iPhonePlayerProfileSummary,
						iPhonePlayerProfileFFS,
						iPhoneRosterIndex,
						iPhoneRosterProfileImage
					]}
				/>
				<div className="text-center pt-14">
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
						{VALUE_PILLARS.map((pillar) => (
							<div key={pillar.title} className="px-4">
								<i
									className={`icon ${pillar.icon} fa-3x mb-4 text-gray-700`}
									aria-hidden
								/>
								<h3 className="text-xl font-semibold mb-2">{pillar.title}</h3>
								<p className="text-gray-600 text-sm leading-relaxed">
									{pillar.description}
								</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* CTA band */}
			<section className="bg-blue-600 text-white py-16 px-6">
				<div className="container mx-auto max-w-3xl text-center">
					<h2 className="text-3xl md:text-4xl font-bold mb-4">
						Ready to work, rise, and dominate?
					</h2>
					<p className="text-lg text-blue-100 mb-8">
						Join thousands of fantasy managers who refuse to phone it in—on the field
						or in the gym. Pick a plan and start winning today.
					</p>
					<div className="flex flex-wrap justify-center gap-4">
						<Link
							to="/signup"
							className="inline-block rounded-lg bg-white px-8 py-3 text-lg font-semibold text-blue-600 hover:bg-blue-50 transition-colors"
						>
							Get started
						</Link>
						<a
							href="#our-pricing"
							onClick={(e) => {
								e.preventDefault();
								document
									.querySelector('#our-pricing')
									?.scrollIntoView({ behavior: 'smooth' });
							}}
							className="inline-block rounded-lg border-2 border-white px-8 py-3 text-lg font-semibold text-white hover:bg-white/10 transition-colors"
						>
							View pricing
						</a>
					</div>
				</div>
			</section>

			{/* Pricing */}
			<section id="our-pricing" className="bg-white py-16 px-6 scroll-mt-8">
				<h2 className="mb-4 text-center font-bold text-4xl md:text-6xl">Our Pricing</h2>
				<p className="text-xl mb-10 text-center text-gray-600 max-w-2xl mx-auto">
					Choose the plan that matches your ambition—from essential roster tools to the
					full Ultimate suite with AI insights and maximum roster capacity.
				</p>
				<PricingTable
					className="overflow-y-none"
					stripe_product_essential_id={stripe_product_essential_id}
					stripe_product_advanced_id={stripe_product_advanced_id}
					stripe_product_ultimate_id={stripe_product_ultimate_id}
				/>
			</section>

			{/* FAQ */}
			<section className="container mt-8 py-16 px-6 pb-24">
				<div className="grid grid-cols-1 md:grid-cols-2 gap-12">
					<div>
						<h3 className="text-3xl md:text-5xl font-bold mb-4">
							Your questions, <span className="block text-blue-600">answered</span>
						</h3>
						<p className="text-gray-600 text-lg">
							Everything you need to know about Fantasy Fitness—the fantasy platform
							and lifestyle app in one.
						</p>
					</div>
					<div>
						<Accordion
							title="Is Fantasy Fitness right for you?"
							body="Fantasy Fitness is built for competitive fantasy football players who also care about fitness, nutrition, and self-improvement. If you want deeper data than your league platform provides—and tools to stay sharp off the field—you are in the right place."
						/>
						<Accordion
							title="What lifestyle features are included?"
							body="Members get access to immersive hubs: Fitness Hub (profiles and Challenge Wire), Nutrition Lab (AI fridge scanning and recipe ideas), The Classroom (film room and whiteboards), Best Ball Zone, and Front Office mode—a gamified command center for your fantasy life."
						/>
						<Accordion
							title="What features are included in each plan?"
							body="Essential covers core roster management and prospect tools. Advanced adds AI Insights, Draft Tracker, and exposure tracking. Ultimate unlocks the largest roster limits and the complete toolset. All plans include lifestyle hubs and an ad-free experience."
						/>
						<Accordion
							title="How do I upgrade or downgrade my plan?"
							body="You can easily upgrade or downgrade your plan through your account settings at any time."
						/>
						<Accordion
							title="How do I get support if I need help?"
							body="Contact matt@fantasyfitness.io for any support inquiries."
						/>
					</div>
				</div>
			</section>
		</div>
	);
}
